// src/components/ProtectedRoute.js - COMPLETE FIXED VERSION
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { Box, CircularProgress, Typography } from "@mui/material";

export default function ProtectedRoute({ children, allowedRoles }) {
    const { user, userProfile, loading } = useAuth();
    const location = useLocation();

    // Show loading state while checking authentication
    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100vh",
                    background: "linear-gradient(135deg, #003366 0%, #004d99 100%)",
                }}
            >
                <CircularProgress sx={{ color: "#C1272D", mb: 2 }} size={60} />
                <Typography variant="h6" sx={{ color: "#fff" }}>
                    Loading...
                </Typography>
            </Box>
        );
    }

    // Redirect to login if not authenticated
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Redirect to appropriate dashboard based on role if no userProfile yet
    if (!userProfile) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100vh",
                    background: "linear-gradient(135deg, #003366 0%, #004d99 100%)",
                }}
            >
                <CircularProgress sx={{ color: "#C1272D", mb: 2 }} size={60} />
                <Typography variant="h6" sx={{ color: "#fff" }}>
                    Setting up your profile...
                </Typography>
            </Box>
        );
    }

    // Gatekeeping: block dashboard and app access until onboarding is complete, except for admins
    const isAdmin = userProfile.role === "admin";
    const unlocked = !!userProfile.dashboardUnlocked;

    // Whitelist paths that are allowed during onboarding
    const allowedDuringOnboarding = [
        "/onboarding",
        "/assessment",
        "/unlock",
        "/student/learning/paths",
        "/student/learning/progress",
        "/student/learning/paths/",
        "/student/learning/test/"
    ];
    const path = location?.pathname || "";
    const isWhitelisted = allowedDuringOnboarding.some(p => path.startsWith(p));

    if (!isAdmin && !unlocked && !isWhitelisted) {
        return <Navigate to="/onboarding" replace />;
    }

    // Check if user role is allowed
    if (allowedRoles && !allowedRoles.includes(userProfile.role)) {
        // Redirect to appropriate dashboard based on user's actual role
        const redirectPath =
            userProfile.role === "admin"
                ? "/admin"
                : userProfile.role === "startup"
                    ? "/startup"
                    : "/student";
        return <Navigate to={redirectPath} replace />;
    }

    // User is authenticated and authorized
    return <>{children}</>;
}
